"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parsePersonName = exports.isSurnameParticle = void 0;
const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v', 'phd', 'md', 'esq', 'cpa']);
const PREFIXES = new Set(['dr', 'mr', 'mrs', 'ms', 'mx']);
// Common surname particles. Used to detect compound last names like "van der Berg"
// in non-comma input. Matched case-insensitively so ALL-CAPS payroll feeds (common
// from Gusto / ADP / Rippling exports) parse the same as mixed-case ones.
const LAST_NAME_PARTICLES = new Set([
    'van',
    'von',
    'der',
    'den',
    'de',
    'del',
    'della',
    'di',
    'da',
    'du',
    'la',
    'le',
    'los',
    'las',
    'dos',
    'do',
    'ten',
    'ter',
    'bin',
    'ibn',
    'ben',
    'al',
    'el',
]);
const stripTrailingPunctuation = (token) => token.replace(/[.,;]+$/u, '');
const normalizeToken = (token) => stripTrailingPunctuation(token).toLowerCase();
const isSuffix = (token) => SUFFIXES.has(normalizeToken(token));
const isPrefix = (token) => PREFIXES.has(normalizeToken(token));
/** Whether a token is a surname particle rather than a name in its own right, as in "van der Berg". */
const isSurnameParticle = (token) => LAST_NAME_PARTICLES.has(normalizeToken(token));
exports.isSurnameParticle = isSurnameParticle;
const splitWhitespace = (input) => input.split(/\s+/u).filter((t) => t.length > 0);
/**
 * Parse a person name string into a normalized full form and a last-name key suitable for
 * cohort indexing. Handles both "First Last" and "Last, First" formats and strips common
 * honorific prefixes (Dr/Mr/Mrs/Ms/Mx) and generational/professional suffixes
 * (Jr/Sr/II/III/IV/V/PhD/MD/Esq/CPA).
 *
 * Returns null if the input is empty or contains no recognizable last token.
 */
const parsePersonName = (raw) => {
    if (raw === null || raw === undefined) {
        return null;
    }
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        return null;
    }
    let firstTokens;
    let lastTokens;
    // Detect "Last, First" vs "First Last, Suffix": only treat the comma as a Last/First
    // separator when the post-comma chunk contains at least one non-suffix token.
    const commaIndex = trimmed.indexOf(',');
    const postCommaTokens = commaIndex !== -1 ? splitWhitespace(trimmed.slice(commaIndex + 1)).filter((t) => !isPrefix(t)) : [];
    const isLastFirstFormat = commaIndex !== -1 && postCommaTokens.some((t) => !isSuffix(t));
    if (isLastFirstFormat) {
        const lastChunk = trimmed.slice(0, commaIndex);
        lastTokens = splitWhitespace(lastChunk).filter((t) => !isSuffix(t));
        firstTokens = postCommaTokens.filter((t) => !isSuffix(t));
    }
    else {
        const allTokens = splitWhitespace(trimmed).filter((t) => !isPrefix(t) && !isSuffix(t));
        if (allTokens.length === 0) {
            return null;
        }
        // If a known particle ("van", "de", "von", ...) appears mid-name with at least
        // one token on each side, treat everything from that particle onward as the
        // last name. This produces the same result as the comma format for compound
        // surnames so both formats land in the same last-name cohort.
        const particleIdx = allTokens.findIndex((t) => (0, exports.isSurnameParticle)(t));
        if (particleIdx >= 1 && particleIdx < allTokens.length - 1) {
            firstTokens = allTokens.slice(0, particleIdx);
            lastTokens = allTokens.slice(particleIdx);
        }
        else {
            lastTokens = [allTokens[allTokens.length - 1]];
            firstTokens = allTokens.slice(0, -1);
        }
    }
    if (lastTokens.length === 0) {
        return null;
    }
    const lastNormalized = lastTokens.map(normalizeToken).join(' ');
    if (lastNormalized.length === 0) {
        return null;
    }
    const firstNormalized = firstTokens
        .map(normalizeToken)
        .filter((t) => t.length > 0)
        .join(' ');
    const fullParts = [...firstTokens, ...lastTokens].map(normalizeToken).filter((t) => t.length > 0);
    const full = fullParts.join(' ');
    return { full, first: firstNormalized, last: lastNormalized };
};
exports.parsePersonName = parsePersonName;
